import React from 'react';
import styled from 'styled-components';
import {
  IconWithText,
  IconArrowRight,
  cssSpacing,
  titleSmall,
  screenSmMax,
  white,
  Text,
  TooltipTrigger,
  Tooltip,
  cssColorValue,
} from '@spotify-internal/encore-web';
import type { ProviderService } from './types';
import { useWidth } from '../ui';
import { ContentfulImage } from '../Contentful';
import { useT } from '@spotify-internal/i18n-core';
import { TRACK_CAT } from './constants';
import { InteractiveElement } from '../../utils/InteractiveElement';

const IMG_SIZE = '72px';

const TileContainer = styled.li<{ ref: any }>`
  width: 100%;
  display: flex;
  flex-flow: row wrap;
  padding: ${cssSpacing('base')} 0;

  a {
    text-decoration: none;
    color: ${cssColorValue('textSubdued')};

    &:hover,
    &:focus:not(:disabled),
    &:active:not(:disabled),
    &.highlight {
      text-decoration: none;
      color: ${cssColorValue('textBase')};
    }
  }
`;

const Logo = styled.div`
  &,
  img {
    min-height: ${IMG_SIZE};
    min-width: ${IMG_SIZE};
    max-height: ${IMG_SIZE};
    max-width: ${IMG_SIZE};
    object-fit: contain;
    padding: 0;
  }
`;

const Name = styled.div`
  width: 156px;
  align-self: flex-start;
  height: ${IMG_SIZE};
  display: flex;
  flex-direction: column;
  align-content: flex-start;
  margin: 0 ${cssSpacing('tighter')};

  div {
    margin: 0;
    a {
      text-decoration: none;
    }
    h3 {
      margin: 0;
      width: 100%;
      ${titleSmall}
      margin-bottom: ${cssSpacing('base')};
    }
  }
`;

const Description = styled.div`
  max-width: 55%;
  @media (max-width: ${screenSmMax}) {
    padding: ${cssSpacing('base')} 0;
    max-width: 100%;
  }
`;

/* TODO: this button design is too hardcoded to match the old provider directory design */
const Preferred = styled.div`
  background: ${cssColorValue('essentialAnnouncement')};
  color: ${white};
  align-self: flex-start;
  border-radius: 4px;
  padding: 0 7px 3px 7px;
`;

const OutboundLink = styled.div`
  margin: 0 ${cssSpacing('looser-4')};
  && {
    @media (max-width: ${screenSmMax}) {
      margin-left: 0;
    }
  }
`;

export const ServiceTile: React.FC<ProviderService> = (
  service: ProviderService,
) => {
  const { name, description, image, preferred, url } = service;
  const [showTooltip, setShowTooltip] = React.useState(false);
  const { width, widthRef } = useWidth();
  const trackLabel = `Distributor Link - ${name} - ${url}`;
  const t = useT();

  return (
    <TileContainer ref={widthRef}>
      <Logo>
        <InteractiveElement
          target="_blank"
          href={url}
          trackingCategory={TRACK_CAT}
          trackingLabel={trackLabel}
        >
          {image && (
            <ContentfulImage asset={image} imageStyle={{ borderRadius: 4 }} />
          )}
        </InteractiveElement>
      </Logo>

      <Name>
        <div>
          {name && (
            <InteractiveElement
              target="_blank"
              href={url}
              trackingCategory={TRACK_CAT}
              trackingLabel={trackLabel}
            >
              <h3>{name}</h3>
            </InteractiveElement>
          )}
        </div>
        {preferred && (
          <Preferred>
            <TooltipTrigger
              overlay={
                showTooltip && (
                  <Tooltip>
                    {t(
                      'FRODOR_PROVIDER_DIRECTORY_SERVICE_TILE_TIP',
                      'These providers meet our highest standards for providing quality metadata and protecting against infringement.',
                      'A tool tip that shows on hover',
                    )}
                  </Tooltip>
                )
              }
              placement={
                width < 480 ? TooltipTrigger.bottom : TooltipTrigger.right
              }
              onShow={() => setShowTooltip(true)}
              onHide={() => setShowTooltip(false)}
            >
              <Text variant="marginalBold">
                {t(
                  'FRODOR_PROVIDER_DIRECTORY_PREFERRED_CTA',
                  'preferred',
                  'Button text',
                )}
              </Text>
            </TooltipTrigger>
          </Preferred>
        )}
      </Name>
      <Description>{description}</Description>
      <OutboundLink>
        <InteractiveElement
          target="_blank"
          href={url}
          trackingCategory={TRACK_CAT}
          trackingLabel={trackLabel}
        >
          <IconWithText
            icon={() => <IconArrowRight iconSize={16} />}
            rightAlign
          >
            <Text variant="bodySmallBold">
              {t('FRODOR_PROVIDER_DIRECTORY_VISIT_CTA', 'Visit', 'Button text')}
            </Text>
          </IconWithText>
        </InteractiveElement>
      </OutboundLink>
    </TileContainer>
  );
};
